import { Button, ButtonLink, DataTable } from 'components';
import { getQueryError, handleApiResponse, ShortOption, useAppSelector, useSystem } from 'store';
import { useAlert, useToast } from 'context';
import { AddEntityMappingDialog } from 'pages/Settings';
import { userIs } from 'utils';
import { UserRole } from 'models';
import { useSetUndoReconciliationMutation } from 'store/services/MappingService';

interface ExistingEntityTableProps {
	aliasesData: AliasesData[];
	save: (fromEntity: number, toEntity: number) => Promise<void>;
	entityOptions: ShortOption[];
	title: string;
	tooltipContent: string;
	goToHelpID: string;
	entityName: string;
	pickableEntities: ShortOption[];
	keepPageIndexOnLeave?: string;
}

export interface AliasesData {
	target_id: number;
	alias_id: number;
	id: number;
	alias_name: string;
	target_name: string;
	inserted_at: Date;
}

export function ExistingEntityTable({
	aliasesData,
	entityOptions,
	save,
	title,
	tooltipContent,
	keepPageIndexOnLeave,
	goToHelpID,
	entityName,
	pickableEntities,
}: ExistingEntityTableProps) {
	const toast = useToast();
	const { getAlertResponse } = useAlert();
	const { data } = useSystem();
	const { selectedSystem } = useAppSelector((state) => state.userState);

	const [setUndoReconcilation, { isLoading }] = useSetUndoReconciliationMutation();
	const setUndoAlias = async (alias_id: number) => {
		const response = await setUndoReconcilation({
			healthsystem_id: selectedSystem,
			alias_id: alias_id,
		});

		handleApiResponse(response, {
			success: () => {
				toast.createToast({
					title: `Alias undo successful.`,
					variant: 'success',
				});
			},
			error: (payload) => {
				toast.createToast({
					title: getQueryError(payload),
					variant: 'error',
				});
			},
		});
	};

	const isAdministrator = userIs([UserRole.admin, UserRole.healthsystem_admin], data?.user);

	return (
		<div className='h-50'>
			<DataTable
				title={title}
				tooltipContent={tooltipContent}
				goToHelpID={goToHelpID}
				keepPageIndexOnLeave={keepPageIndexOnLeave}
				minHeight={'28em'}
				headerContentRight={
					<AddEntityMappingDialog
						entityOptions={entityOptions}
						save={save}
						aliasIDs={aliasesData.map((d) => d.target_id).concat(aliasesData.map((d) => d.alias_id))}
						entityName={entityName}
						pickableEntites={pickableEntities}
					>
						<Button sizeX='sm'>Add Mapping</Button>
					</AddEntityMappingDialog>
				}
				columns={[
					{
						header: `Existing ${entityName} Name`,
						accessorKey: 'target_name',
						enableGlobalFilter: true,
						cell: ({ row }) => (
							<span className='py-1 text-p2 text-center'>
								{entityOptions.find((d) => d.id === row.original.target_id)?.name ?? (
									<span className='text-gray-400 italic text-p2'>No results found for this name</span>
								)}
							</span>
						),
					},
					{
						header: 'Multiple Aliases',
						accessorKey: 'alias_name',
						cell: ({ row }) => <span className='py-1 text-p2 text-center'>{row.original.alias_name}</span>,
						enableGlobalFilter: true,
					},
					{
						header: 'Date of Mapping',
						accessorKey: 'inserted_at',
						cell: ({ row }) => (
							<span className='py-1 text-p2 text-center'>
								{new Date(row.original.inserted_at).toLocaleString('en-US', {
									month: 'numeric',
									day: 'numeric',
									year: '2-digit',
									hour: 'numeric',
									minute: 'numeric',
									hour12: true,
								})}
							</span>
						),
						sortingFn: (rowA, rowB, columnId) => {
							const a = new Date(rowA.getValue(columnId)).getTime();
							const b = new Date(rowB.getValue(columnId)).getTime();
							return b > a ? 1 : -1;
						},
						enableGlobalFilter: false,
					},
					{
						id: 'actions',
						meta: {
							bodyClass: 'text-left',
						},
						cell: ({ row }) => {
							return isAdministrator ? (
								<div className='transition-opacity opacity-0 group-hover:opacity-100'>
									<ButtonLink
										isWorking={isLoading}
										onClick={async () => {
											// ask the user to confirm the deletion
											const alertResponse = await getAlertResponse({
												title: 'Confirm Undo Reconcilation',
												description: `Are you sure you want to Undo this ${entityName}?`,
												responseOptions: [
													{
														value: 'delete',
														label: 'Delete',
													},
												],
											});

											// handle the response from the user
											if (alertResponse === 'delete') {
												// adds the loading indicator NEXT to the specific item being deleted
												await setUndoAlias(row.original.id);
												// remove the loading indicator next to the user row of user being deleted
											}
										}}
									>
										Delete
									</ButtonLink>
								</div>
							) : null;
						},
					},
				]}
				data={aliasesData}
			/>
		</div>
	);
}

export default ExistingEntityTable;
